<template>
    <div class="email-link-clicked-process-modal">
        <GoalModal
            size="fullscreen"
            :is-open="!hasEmailSequence"
            data-qa="email-link-clicked-modal"
            class="email-link-clicked-modal"
            :title="$t('funnel.edit.goal.link.flow.title')"
            :cell="cell"
            :action-button-label="$t('funnel.edit.goal.link.flow.save.button')"
            @close-modal="closeModal"
            @update-config="saveCellValue"
        >
            <template #goalParameters>
                <div class="email-link-list-lead">
                    <DsListItem
                        v-for="(item, i) in sequences"
                        :key="i"
                        data-qa="email-link-clicked-sequence-list"
                        active
                    >
                        <template
                            #trailing
                        >
                            <div class="email-link-list">
                                <div class="email-link-clicked-card">
                                    <h1>
                                        <b> {{ item.title }} </b>
                                    </h1>
                                    <h2>
                                        {{ item.description }}
                                    </h2>
                                </div>
                                <DsListItem
                                    v-for="(email, j) in item.emails"
                                    :key="j"
                                    data-qa="email-link-clicked-email-list"
                                    :title="email.title"
                                    clickable
                                    border-style="full"
                                    active
                                    @click.native="assignEmailTemplate(email)"
                                >
                                    <template #shortDescription>
                                        {{ email.description }}
                                    </template>
                                    <template #leading>
                                        <DsIcon name="email" class="email-icon" />
                                    </template>

                                    <template #trailing>
                                        <DsIcon name="chevron-right" />
                                    </template>
                                </DsListItem>
                            </div>
                        </template>
                    </DsListItem>
                </div>
                <div class="email-link-preview">
                    <DsHtmlPreview
                        id="emailLinkPreviewFrame"
                        hide-header
                        data-qa="email-link-clicked-preview"
                        :html="htmlBody"
                    />
                </div>
            </template>
        </GoalModal>
    </div>
</template>

<script>
import GoalModal from '../goalModal/GoalModal.vue';
import { utils } from '../../../utils/legacy-utils';
import { GOALS } from '../../../utils/editor.constants';

import {
    linkMap,
} from './emaillink.api';
import { findChildren, getValidIncomingFlows } from '../../../utils/cell/automation-cell-utils';
import { findIncomingCells } from '../../../utils/cell/automation-edge-utils';

const URL_LINK_CLICK_CONTROLLER = '/app/linkClick/';

export default {
    components: {
        GoalModal,
    },
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        cells: {
            type: Array,
            default: () => ([]),
        },
        cell: {
            type: Object,
            default: () => ({
                value: {
                    linkIds: [],
                },
            }),
        },
    },

    emits: ['close-modal', 'update-cell-value'],

    data() {
        return {
            hasEmailSequence: true,
            htmlBody: null,
            selectedLinks: [],
            sequences: [],
            flows: [],
            flowEmails: [],
        };
    },

    computed: {
        isConfirmEmail() {
            return this.cell?.style === GOALS.GOAL_EMAIL_CONFIRM;
        },
    },

    async mounted() {
        if (this.cell?.meta?.style === GOALS.GOAL_EMAIL_CONFIRM) {
            this.$error({
                message: this.$t('funnel.edit.goal.email.confirmation'),
                bottom: true,
            });
            this.closeModal();

            return;
        }
        this.selectedLinks = [];
        const marketingEmailIds = getValidIncomingFlows(this.cell, this.cells);

        if (this.isConfirmEmail) {
            this.displayConfirmEmailMsg();
        } else if (marketingEmailIds?.length > 0) {
            await this.loadConfig();
            this.hasEmailSequence = false;

            window.addEventListener('message', (e) => {
                if (e.data.detail?.marketingEmailId && e.data.detail?.linkId) {
                    this.handleSelectedLinks(e.data.detail.marketingEmailId, e.data.detail.linkId, e.data.detail.clicked, false, true);
                    this.sequences = this.updateLinkDescription(e.data.detail.marketingEmailId);
                }
            });
        } else {
            this.$error({
                message: this.$t('funnel.edit.goal.link.noEmails'),
                bottom: true,
            });
            this.closeModal();
        }
    },

    methods: {
        displayConfirmEmailMsg() {
            this.$error({
                message: this.$t('funnel.edit.goal.emailConfirm.logic'),
                bottom: true,
            });
            this.closeModal();
        },

        getFlows() {
            return findIncomingCells(this.cell.id, this.cells);
        },

        ensureLongEndsWithL (params, indexFirstSlash) {
            return utils.ensureLongEndsWithL(params.substring(0, indexFirstSlash));
        },
        assignEmailTemplate(email) {
            this.htmlBody = this.addToolTips(email.htmlBodySource, email.marketingEmailId, false, false);
        },

        updateLinkDescription(marketingEmailId) {
            const updatedSequences = this.sequences.map((sequence) => {
                const updatedEmails = sequence.emails.map((email) => {
                    if (`'${email.marketingEmailId}'` === `'${marketingEmailId}'`) {
                        const newDescription = this.getLinksDescription(marketingEmailId, email);

                        return { ...email, description: newDescription };
                    }

                    return email;
                });

                return { ...sequence, emails: updatedEmails };
            });

            return updatedSequences;
        },

        handleSelectedLinks(marketingEmailId, linkId, clicked, isMounting, isRefreshing) {
            if (isMounting) {
                clicked = this.alreadyPicked(linkId);

                if (!this.selectedLinks[`'${marketingEmailId}'`]) {
                    this.selectedLinks[`'${marketingEmailId}'`] = [{ id: linkId }];
                }

                if (clicked && !this.selectedLinks[`'${marketingEmailId}'`].some((obj) => obj.id === linkId)) {
                    this.selectedLinks[`'${marketingEmailId}'`].push({ id: linkId });
                }

                if (!clicked) {
                    this.selectedLinks[`'${marketingEmailId}'`] = this.selectedLinks[`'${marketingEmailId}'`]?.filter((item) => item.id !== linkId);
                }
            } else {
                const shouldAddLink = isRefreshing && clicked;
                const shouldDeleteLink = isRefreshing && !clicked;

                if (shouldAddLink) {
                    if (!this.selectedLinks[`'${marketingEmailId}'`]) {
                        this.selectedLinks[`'${marketingEmailId}'`] = [{ id: linkId }];
                    } else {
                        this.selectedLinks[`'${marketingEmailId}'`].push({ id: linkId });
                    }
                }

                if (shouldDeleteLink) {
                    this.selectedLinks[`'${marketingEmailId}'`] = this.selectedLinks[`'${marketingEmailId}'`]?.filter((item) => item.id !== linkId);
                }

                if (!shouldAddLink && !shouldDeleteLink) {
                    clicked = this.selectedLinks[`'${marketingEmailId}'`].some((obj) => obj.id === linkId);
                }
            }

            return clicked;
        },

        async getLinksMap(marketingEmailId) {
            return linkMap(marketingEmailId);
        },

        countATags(htmlBody) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(htmlBody, 'text/html');
            const aTags = doc.querySelectorAll('a');

            let aTagCounter = 0;

            aTags.forEach((aTag) => {
                const href = aTag.getAttribute('href');
                const indexController = href.indexOf(URL_LINK_CLICK_CONTROLLER);

                if (indexController > -1) {
                    const params = href.substring(indexController + URL_LINK_CLICK_CONTROLLER.length, href.length);
                    const indexFirstSlash = params.indexOf('/');

                    if (indexFirstSlash > -1) {
                        aTagCounter += 1;
                    }
                }
            });

            return aTagCounter;
        },

        addToolTips(htmlBody, marketingEmailId, isMounting, isRefreshing) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(htmlBody, 'text/html');
            const aTags = doc.querySelectorAll('a');

            aTags.forEach((aTag) => {
                const { parentNode } = aTag;
                const table = document.createElement('table');
                const topRow = document.createElement('tr');
                const bottomRow = document.createElement('tr');
                const linkTd = document.createElement('td');
                const checkboxTd = document.createElement('td');
                const linkDiv = document.createElement('div');
                const checkbox = doc.createElement('input');
                const href = aTag.getAttribute('href');
                const indexController = href.indexOf(URL_LINK_CLICK_CONTROLLER);

                if (indexController > -1) {
                    const params = href.substring(indexController + URL_LINK_CLICK_CONTROLLER.length, href.length);
                    const indexFirstSlash = params.indexOf('/');

                    if (indexFirstSlash > -1) {
                        const linkId = this.ensureLongEndsWithL(params, indexFirstSlash);
                        const toolTipArrow = document.createElement('div');

                        linkDiv.style.position = 'relative';
                        linkDiv.style.display = 'inline-block';
                        linkDiv.style.padding = '5px 8px';
                        linkDiv.style.backgroundColor = 'white';
                        linkDiv.style.border = '2px solid black';
                        linkDiv.style.borderRadius = '8px';
                        linkDiv.style.color = 'black';
                        linkDiv.style.padding = '5px';
                        linkDiv.style.top = '4px';

                        toolTipArrow.style.position = 'absolute';
                        toolTipArrow.style.width = '0';
                        toolTipArrow.style.height = '0';
                        toolTipArrow.style.borderLeft = '10px solid transparent';
                        toolTipArrow.style.borderRight = '10px solid transparent';
                        toolTipArrow.style.borderBottom = '10px solid black';
                        toolTipArrow.style.top = '-10px';
                        toolTipArrow.style.left = '50%';
                        toolTipArrow.style.transform = 'translateX(-50%)';

                        linkDiv.appendChild(toolTipArrow);
                        linkDiv.appendChild(checkbox);

                        checkbox.type = 'checkbox';
                        checkbox.setAttribute('linkId', linkId);
                        checkbox.setAttribute('marketingEmailId', marketingEmailId);
                        checkbox.setAttribute('onClick', `window.parent.postMessage({ detail: { linkId: '${linkId}', clicked: this.checked, marketingEmailId: '${marketingEmailId}' }}, '*');`);

                        table.style.display = 'inline-block';

                        linkTd.appendChild(aTag);
                        topRow.appendChild(linkTd);
                        checkboxTd.appendChild(linkDiv);
                        bottomRow.appendChild(checkboxTd);
                        table.appendChild(topRow);
                        table.appendChild(bottomRow);

                        parentNode.appendChild(table);

                        const alreadyPicked = this.handleSelectedLinks(marketingEmailId, linkId, null, isMounting, isRefreshing);

                        if (alreadyPicked) {
                            checkbox.setAttribute('checked', alreadyPicked);
                        } else {
                            checkbox.removeAttribute('checked');
                        }
                    }
                }
            });

            return doc.body.innerHTML;
        },

        alreadyPicked(linkId) {
            const cellValue = this.cell.value || {};
            const { linkIds } = cellValue;

            return linkIds && linkIds.includes(linkId);
        },

        getEmails(flowId) {
            return findChildren(flowId, this.flowEmails);
        },

        async loadConfig() {
            this.flowEmails = getValidIncomingFlows(this.cell, this.cells);
            this.flows = this.getFlows();

            if (this.flowEmails && this.flows) {
                this.sequences = await Promise.all(this.flows.map(async (flow) => {
                    const { name } = flow.value || {};
                    const flowTitle = name || this.$t('funnel.edit.label.flow');

                    const flowId = flow.id;
                    const emails = this.getEmails(flowId);

                    if (!emails || emails.length === 0) {
                        return null;
                    }

                    const emailsDetail = await Promise.all(emails.map(async (email) => {
                        const { marketingEmailId, name: emailCellName } = email.value || {};

                        const emailTitle = emailCellName || this.$t(`funnel.edit.label.flow.${email.style || 'unlayerEmail'}`);
                        const maps = await this.getLinksMap(marketingEmailId);

                        const emailDetail = Object.keys(maps).map((key) => ({
                            key,
                            fromAddress: maps[key].fromAddress,
                            htmlBody: this.addToolTips(maps[key].htmlBody, marketingEmailId, true, false),
                            htmlBodySource: maps[key].htmlBody,
                            subject: maps[key].subject,
                            id: maps[key].id,
                            title: emailTitle,
                            description: this.getLinksDescription(marketingEmailId, maps[key]),
                            marketingEmailId,
                        }));

                        return emailDetail[0];
                    }));

                    const sequence = {
                        description: this.$t('funnel.edit.goal.link.flow.selected.list.text'),
                        title: flowTitle,
                        emails: emailsDetail.filter(Boolean),
                    };

                    sequence.emails.sort((a, b) => a.id - b.id);

                    return sequence;
                }));

                this.sequences = this.sequences.filter(Boolean);
                this.sequences.sort((a, b) => a.emails[0].id - b.emails[0].id);
                this.htmlBody = this.sequences[0]?.emails[0]?.htmlBody;
            }
        },


        closeModal() {
            this.$emit('close-modal');
        },

        getLinksDescription(marketingEmailId, email) {
            let linksNum = 0;

            if (this.selectedLinks[`'${marketingEmailId}'`]) {
                linksNum = this.selectedLinks[`'${marketingEmailId}'`].length;
            }

            return `${linksNum} ${this.$t('funnel.edit.goal.link.flow.selected.of')} ${this.countATags(email.htmlBody)} ${this.$t('funnel.edit.goal.link.flow.selected')}`;
        },

        async saveCellValue() {
            const cell = { ...this.cell };

            cell.value = cell.value || {};

            const arrayOfIds = Object.keys(this.selectedLinks).flatMap((key) => {
                return this.selectedLinks[key].map((item) => item.id);
            });

            cell.value.linkIds = arrayOfIds;


            this.$emit('update-cell-value', { cell });
        },
    },
};
</script>

<style lang="scss">
.email-link-clicked-process-modal {
    .goal-summary-label {
        display: none;
    }
}
</style>

<style lang="scss" scoped>

.email-link-clicked-process-modal {
    .list-item-info {
        position: relative;
        width: px-to-rem(480);
    }

    .border-partial .list-item-info {
        border-bottom: none;
        padding-bottom: var(--list-item-padding, $spacing-100)
    }

    .item-text {
        float: top;
    }

    .trailing-slot {
        float: bottom;
    }

    .email-link-list {
        width: px-to-rem(500);
        float: left;
        font-size: px-to-rem(7);
        padding: px-to-rem(7) px-to-rem(16) px-to-rem(0) px-to-rem(50);
        gap: px-to-rem(10);
        grid-column-gap: $spacing-200;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        border-radius: px-to-rem(12);
        background: var(--Paper, #FFF);
        box-shadow: px-to-rem(0) px-to-rem(1) px-to-rem(3) px-to-rem(0) rgba(0, 0, 0, 0.20),
            px-to-rem(0) px-to-rem(2) px-to-rem(1) px-to-rem(-1) rgba(0, 0, 0, 0.12),
            px-to-rem(0) px-to-rem(1) px-to-rem(1) px-to-rem(0) rgba(0, 0, 0, 0.14);
    }

    .email-link-preview {
        width: px-to-rem(900);
        float: left;
        font-size: px-to-rem(9);
        padding: px-to-rem(30) px-to-rem(16) px-to-rem(0) px-to-rem(30);
        align-items: flex-start;
        gap: px-to-rem(10);
        align-self: stretch;
    }

    .email-link-clicked-sequence-list {
        margin-top: px-to-rem(480);
        padding: px-to-rem(20);
        display: block;
    }

    .email-link-list-lead {
        width: px-to-rem(600);
        float: left;
        padding: px-to-rem(0) px-to-rem(16) px-to-rem(0) px-to-rem(72);
        gap: px-to-rem(10);
        grid-column-gap: $spacing-200;
    }

    .email-link-clicked-card > h1{
        margin-left: px-to-rem(-30);
        margin-top: px-to-rem(10);
        font-size: px-to-rem(18);
    }

    .email-link-clicked-card > h2{
        margin-left: px-to-rem(-30);
        margin-top: px-to-rem(10);
        font-size: px-to-rem(15);
    }
}

</style>
